import cloneDeep from 'lodash/cloneDeep';
import memoize from '@framework/data-helpers/memoize';
import sortBy from 'lodash/sortBy';
import capitalize from '@framework/data-helpers/string/capitalize';
import { toSlug } from '@framework/url/helpers/toSlug';
import URL from '@/creditflowresearch/Router/urlConstants';
import {
  INDUSTRIES_COLUMNS_CONFIG,
  SEARCH_RESULT_FIRST_COLUMN_CONFIG,
  SECTORS_COLUMNS_CONFIG,
  SEPARATOR,
} from '@/creditflowresearch/application/SearchHeaderMenu/constants';
import ISector from '@/creditflowresearch/domain/vo/IDealSector';
import IIndustry from '@/creditflowresearch/domain/vo/IIndustry';
import { IProduct } from '@/creditflowresearch/domain/vo/Product';
import { IRegion } from '@/creditflowresearch/domain/vo/Region';
import ITools from '@/creditflowresearch/domain/vo/ITools';
import CorporateProduct from '@/creditflowresearch/domain/Product/entities/CorporateProduct';
import { HeaderMappingItem } from '@/creditflowresearch/application/actions/profile/helpers/types';
import { ISearchResult, ISearchResultSection, ISearchResultItem } from '@/creditflowresearch/domain/vo/ISearch';
import { IDictionarySector, IDictionaryIndustryMapping } from '@/creditflowresearch/domain/vo/IDictionary';

const FILTER_TYPES = {
  SECTOR: 'sector',
  INDUSTRY: 'industry',
} as const;

const TOOL_ROUTES = {
  BONDS: 'bonds',
  EE: 'product/us/abs/ee',
  PIPELINE: 'product/us/abs/pipeline',
  RESEARCH: 'nrsro/research',
} as const;

const TOOL_PATHS = {
  BONDS: 'bonds',
  EE: 'ee',
  PIPELINE: 'pipeline',
  RESEARCH: 'research',
} as const;

const CHILDREN_FIELD_NAMES = {
  SUBSECTOR_LIST: 'subsectorList',
  SUBINDUSTRY_LIST: 'subindustryList',
} as const;

const REGIONS_FIELD_PREFIX = 'region';
const PRODUCT_FIELD_PREFIX = 'product';
const REGIONS_KEY = 'regions';

interface IToolConfig {
  route: string,
  skipProductRegionAppend: boolean,
}

export interface IProductMapping {
  [productId: string]: {
    [regionId: string]: Record<string, unknown>,
  },
}

interface ISearchResultWithLinks extends ISearchResult {
  sectorAndSubsectors?: ISearchResultSection | null,
  industryAndSubindustries?: ISearchResultSection | null,
  issuers?: ISearchResultSection | null,
  parents?: ISearchResultSection | null,
  hasData: boolean,
  hasFirstColumnData: boolean,
}

const getNamesStringFromArray = (array: IProduct[] | IRegion[]): string =>
  array.map((item) => item.abbreviation).join(',');

const getCorporateProductsNames = memoize((products: IProduct[]): string => {
  const corporateProducts = products.filter(({ id }) => CorporateProduct.CORPORATE_PRODUCT_IDS.includes(id));
  return getNamesStringFromArray(corporateProducts);
});

const buildSectionPathname = (item: ISearchResultItem, pathnameSuffix?: string): string => {
  const parentSlug = toSlug(item.additional?.name || '');
  const childSlug = item.additional?.childName ? toSlug(item.additional.childName) : '';

  if (childSlug) {
    return `${ parentSlug }-${ childSlug }-${ pathnameSuffix }`;
  }

  return `${ parentSlug }-${ pathnameSuffix }`;
};

const mapSectionLinks = (
  section: ISearchResultSection | null | undefined,
  search: string,
  pathnameSuffix?: string,
): ISearchResultSection | null | undefined => {
  if (!section || section.totalCount <= 0 || !section.collection) {
    return section;
  }

  const mappedSection = { ...section };
  mappedSection.collection = section.collection.map((item: ISearchResultItem) => {
    const mappedItem = { ...item };
    mappedItem.link = {
      pathname: item.url || buildSectionPathname(item, pathnameSuffix),
      search,
    };
    return mappedItem;
  });

  return mappedSection;
};

export interface IMenuProfile {
  id: string,
  name: string,
  active: boolean,
  color: string,
  link: string,
  abbreviation?: string,
  children: Array<{
    id: string,
    name: string,
    active: boolean,
    link: string,
  }>,
}

export interface IMappingResult {
  defaultProduct?: IProduct,
  defaultRegion?: IRegion,
  mappedData: Record<string, HeaderMappingItem>,
  activeMapping?: HeaderMappingItem,
}

class DictionaryMappingService {
  static prepareSectorsMenu(sectors: ISector[] | []): IMenuProfile[][][] {
    const preparedData = DictionaryMappingService.prepareData(
      sectors,
      URL.SECTOR_SUFFIX,
      CHILDREN_FIELD_NAMES.SUBSECTOR_LIST,
    );
    const distributedColumns = DictionaryMappingService.distributeDataByColumns(preparedData, SECTORS_COLUMNS_CONFIG);
    return DictionaryMappingService.convertColumnsToRows(distributedColumns);
  }

  static prepareIndustriesMenu(industries: IIndustry[] | []): IMenuProfile[][][] {
    const preparedData = DictionaryMappingService.prepareData(
      industries,
      URL.INDUSTRY_SUFFIX,
      CHILDREN_FIELD_NAMES.SUBINDUSTRY_LIST,
    );
    const distributedColumns =
      DictionaryMappingService.distributeDataByColumns(preparedData, INDUSTRIES_COLUMNS_CONFIG);
    return DictionaryMappingService.convertColumnsToRows(distributedColumns);
  }

  private static buildChildLink(parentName: string, childName: string, suffix: string): string {
    return `${ toSlug(parentName) }-${ toSlug(childName) }-${ suffix }`;
  }

  private static mapChildrenToMenuItems(
    children: Record<string, { id: string, name: string }>,
    parentName: string,
    suffix: string,
  ): IMenuProfile['children'] {
    return Object.values(children).map((child) => ({
      id: child.id,
      name: child.name,
      active: false,
      link: DictionaryMappingService.buildChildLink(parentName, child.name, suffix),
    }));
  }

  static prepareData(collection: IIndustry[] | ISector[], suffix: string, childrenFieldName: string): IMenuProfile[] {
    return sortBy(
      Object.values(collection).map((element) => ({
        id: element.id,
        name: element.name,
        active: false,
        color: element.color,
        link: `${ toSlug(element.name) }-${ suffix }`,
        abbreviation: element.abbreviation,
        children: DictionaryMappingService.mapChildrenToMenuItems(
          element[childrenFieldName] as Record<string, { id: string, name: string }>,
          element.name,
          suffix,
        ),
      })),
      (item) => item.name,
    );
  }

  static distributeDataByColumns(list: IMenuProfile[], columnsConfig: number[]): IMenuProfile[][] {
    const result: IMenuProfile[][] = [];
    let currentIndex = 0;

    columnsConfig.forEach((numberOfItems) => {
      const endIndex = currentIndex + numberOfItems;
      result.push(list.slice(currentIndex, endIndex));
      currentIndex = endIndex;
    });

    return result;
  }

  private static isEvenIndex(index: number): boolean {
    return index === 0 || index % 2 === 0;
  }

  static convertColumnsToRows(columns: IMenuProfile[][]): IMenuProfile[][][] {
    const result: IMenuProfile[][][] = [];

    columns.forEach((col, index) => {
      if (DictionaryMappingService.isEvenIndex(index)) {
        result.push([col]);
      } else {
        result[result.length - 1].push(col);
      }
    });

    return result;
  }

  static getToolConfigByPath(toolPath: string): IToolConfig {
    const defaultConfig: IToolConfig = {
      route: toolPath,
      skipProductRegionAppend: false,
    };

    const configMap: Record<string, Partial<IToolConfig>> = {
      [TOOL_PATHS.BONDS]: { route: TOOL_ROUTES.BONDS, skipProductRegionAppend: true },
      [TOOL_PATHS.EE]: { route: TOOL_ROUTES.EE },
      [TOOL_PATHS.PIPELINE]: { route: TOOL_ROUTES.PIPELINE },
      [TOOL_PATHS.RESEARCH]: { route: TOOL_ROUTES.RESEARCH, skipProductRegionAppend: true },
    };

    return { ...defaultConfig, ...configMap[toolPath] };
  }

  private static createLookupMap<T extends { id: string }>(items: T[]): Map<string, T> {
    return new Map(items.map((item) => [item.id, item]));
  }

  private static prependRegionFields(region: IRegion): Record<string, unknown> {
    const regionFields: Record<string, unknown> = {};
    Object.entries(region).forEach(([key, value]) => {
      regionFields[`${ REGIONS_FIELD_PREFIX }${ capitalize(key) }`] = value;
    });
    return regionFields;
  }

  private static prependProductFields(product: IProduct): Record<string, unknown> {
    const productFields: Record<string, unknown> = {};
    Object.entries(product).forEach(([key, value]) => {
      if (key !== REGIONS_KEY) {
        productFields[`${ PRODUCT_FIELD_PREFIX }${ capitalize(key) }`] = value;
      }
    });
    return productFields;
  }

  private static getFilterType(settingsMap: Record<string, unknown>): string {
    return settingsMap.sectorBlock === true ? FILTER_TYPES.SECTOR : FILTER_TYPES.INDUSTRY;
  }

  private static processEnabledTools(
    tools: ITools[],
    enabledToolIds: Record<string, boolean>,
  ): ITools[] {
    return tools
      .filter((tool) => !!enabledToolIds[tool.id])
      .map((tool) => ({
        ...tool,
        ...DictionaryMappingService.getToolConfigByPath(tool.path),
      }));
  }

  private static buildMappingKey(productId: string, regionId: string): string {
    return `${ productId }${ SEPARATOR }${ regionId }`;
  }

  private static processRegionMapping(
    product: IProduct,
    region: IRegion,
    settingsMap: Record<string, unknown>,
    tools: ITools[],
  ): HeaderMappingItem {
    const regionFields = DictionaryMappingService.prependRegionFields(region);
    const productFields = DictionaryMappingService.prependProductFields(product);
    const filterType = DictionaryMappingService.getFilterType(settingsMap);
    const enabledTools = DictionaryMappingService.processEnabledTools(
      tools,
      settingsMap.tools as Record<string, boolean>,
    );

    return {
      ...settingsMap,
      ...regionFields,
      ...productFields,
      filterType,
      tools: enabledTools,
    } as HeaderMappingItem;
  }

  static handleMapping(
    products: IProduct[],
    regions: IRegion[],
    tools: ITools[] = [],
    mapping: IProductMapping = {},
  ): IMappingResult {
    const productsMap = DictionaryMappingService.createLookupMap(products);
    const regionsMap = DictionaryMappingService.createLookupMap(regions);
    const results: Record<string, IProduct & { regions: Record<string, HeaderMappingItem> }> = {};
    const mappedData: Record<string, HeaderMappingItem> = {};
    let defaultProduct: IProduct | undefined;
    let defaultRegion: IRegion | undefined;

    Object.entries(mapping).forEach(([productId, productMapping]) => {
      const product = cloneDeep(productsMap.get(productId));
      if (!product) return;

      results[product.id] = { ...product, regions: {} };

      Object.entries(productMapping).forEach(([regionId, settingsMap]) => {
        const region = cloneDeep(regionsMap.get(regionId));
        if (!region) return;

        const regionMapping = DictionaryMappingService.processRegionMapping(
          product,
          region,
          settingsMap,
          tools,
        );

        results[product.id].regions[region.id] = regionMapping;

        if (regionMapping.default === true) {
          defaultProduct = product;
          defaultRegion = region;
        }

        const mappingKey = DictionaryMappingService.buildMappingKey(product.id, region.id);
        mappedData[mappingKey] = cloneDeep(regionMapping);
      });
    });

    const activeMapping = defaultProduct && defaultRegion
      ? mappedData[DictionaryMappingService.buildMappingKey(defaultProduct.id, defaultRegion.id)]
      : undefined;

    return {
      defaultProduct,
      defaultRegion,
      mappedData,
      activeMapping,
    };
  }

  private static checkFirstColumnData(res: ISearchResult): boolean {
    const hasData = res.totalCount > 0;
    if (!hasData || Object.keys(res).length === 0) {
      return false;
    }

    const firstColumnKeys = new Set(SEARCH_RESULT_FIRST_COLUMN_CONFIG.map((item) => item.key));

    return Object.entries(res).some(([key, values]) => {
      const section = values as ISearchResultSection;
      return section?.totalCount > 0 && firstColumnKeys.has(key);
    });
  }

  private static buildSearchQuery(products: IProduct[], regions: IRegion[]): string {
    return `products=${ DictionaryMappingService.getProductsNames(products) }&regions=${ DictionaryMappingService.getRegionsNames(regions) }`;
  }

  static mapSearchResult(res: ISearchResult, products: IProduct[], regions: IRegion[]): ISearchResultWithLinks {
    const hasData = res.totalCount > 0;
    const hasFirstColumnData = hasData && DictionaryMappingService.checkFirstColumnData(res);
    const corporateProductsQuery = `products=${ getCorporateProductsNames(products) }&regions=${ DictionaryMappingService.getRegionsNames(regions) }`;
    const standardQuery = DictionaryMappingService.buildSearchQuery(products, regions);

    return {
      ...res,
      sectorAndSubsectors: mapSectionLinks(
        res.sectorAndSubsectors,
        'products=ABS&regions=USOA',
        URL.SECTOR_SUFFIX,
      ),
      industryAndSubindustries: mapSectionLinks(
        res.industryAndSubindustries,
        corporateProductsQuery,
        'bond-issuance-overview',
      ),
      issuers: mapSectionLinks(res.issuers, standardQuery),
      parents: mapSectionLinks(res.parents, standardQuery),
      hasData,
      hasFirstColumnData,
    };
  }

  private static createSectorMapping(sector: ISector): IDictionarySector {
    return {
      sectorId: sector.id,
      metaTitleName: sector.name,
      pageTitleName: sector.name,
      sectorName: sector.name,
    };
  }

  private static createSubsectorMapping(
    sector: ISector,
    sectorName: string,
    subsector: { id: string, name: string },
  ): IDictionarySector {
    return {
      sectorId: sector.id,
      subsectorId: subsector.id,
      sectorName: sector.name,
      metaTitleName: `${ sector.name } ${ subsector.name }`,
      pageTitleName: `${ sector.name }: ${ subsector.name }`,
      subsectorName: subsector.name,
    };
  }

  static getMappedSectors(sectors: ISector[]): Record<string, IDictionarySector> {
    const mappedSectors: Record<string, IDictionarySector> = {};

    sectors.forEach((sector) => {
      const sectorName = toSlug(sector.name);
      mappedSectors[sectorName] = DictionaryMappingService.createSectorMapping(sector);

      if (sector.subsectorList?.length) {
        sector.subsectorList.forEach((subsector) => {
          const subsectorKey = `${ sectorName }-${ toSlug(subsector.name) }`;
          mappedSectors[subsectorKey] = DictionaryMappingService.createSubsectorMapping(
            sector,
            sectorName,
            subsector,
          );
        });
      }
    });

    return mappedSectors;
  }

  private static createIndustryMapping(industry: IIndustry): IDictionaryIndustryMapping {
    return {
      industryId: industry.id,
      metaTitleName: industry.name,
      pageTitleName: industry.name,
      industryName: industry.name,
    };
  }

  private static createSubindustryMapping(
    industry: IIndustry,
    industryName: string,
    subindustry: { id: string, name: string },
  ): IDictionaryIndustryMapping {
    return {
      industryId: industry.id,
      industryName: industry.name,
      subindustryId: subindustry.id,
      metaTitleName: `${ industry.name } ${ subindustry.name }`,
      pageTitleName: `${ industry.name }: ${ subindustry.name }`,
      subindustryName: subindustry.name,
    };
  }

  static getMappedIndustries(industries: IIndustry[]): Record<string, IDictionaryIndustryMapping> {
    const mappedIndustries: Record<string, IDictionaryIndustryMapping> = {};

    industries.forEach((industry) => {
      const industryName = toSlug(industry.name);
      mappedIndustries[industryName] = DictionaryMappingService.createIndustryMapping(industry);

      if (industry.subindustryList?.length) {
        industry.subindustryList.forEach((subindustry) => {
          const subindustryKey = `${ industryName }-${ toSlug(subindustry.name) }`;
          mappedIndustries[subindustryKey] = DictionaryMappingService.createSubindustryMapping(
            industry,
            industryName,
            subindustry,
          );
        });
      }
    });

    return mappedIndustries;
  }

  static getProductsNames(products: IProduct[]) {
    return getProductsNamesMemoized(products);
  }

  static getRegionsNames(regions: IRegion[]) {
    return getRegionsNamesMemoized(regions);
  }
}

const getProductsNamesMemoized = memoize((products: IProduct[]): string => getNamesStringFromArray(products));
const getRegionsNamesMemoized = memoize((regions: IRegion[]): string => getNamesStringFromArray(regions));

export default DictionaryMappingService;
